import { ajvResolver } from '@hookform/resolvers/ajv';
import { Info as InfoIcon } from '@mui/icons-material';
import { Button, Grid, TableBody, TableHead, TableRow } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { get, get as getObjectValue, isEmpty, omit, values } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CustomButton } from 'src/components/button/Button';
import { Header } from 'src/components/header/Header';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress/Progress';
import { SettingsCategory } from 'src/components/settings';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import {
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { useGetProductVariationTypes, useGetThemes } from 'src/hooks';
import {
  useGetProjectConfig,
  useUpdateProjectConfigs,
} from 'src/hooks/api/projectConfig';
import { ProjectConfigTransaction } from 'src/hooks/api/projectConfig/useUpdateProjectConfigs.interface';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useModal } from 'src/hooks/useModal';
import {
  AccessibilitySettingsSchema,
  ProjectConfigInterface,
} from 'src/interfaces';
import { colorPickerColors, generateOptions, useUserRole } from 'src/utils';
import { lang } from 'src/utils/language';

import {
  FooterContainer,
  SwitchButtonStyle,
} from '../editGlobalSettingsPage/EditGlobalSettingsPage.styled';
import {
  SubContainer,
  SubContent,
} from '../settingsSubNavbar/SettingsSubNavbar.styled';
import { ACCESSIBILITY_SETTINGS_LABELS } from './accessibility_settings_labels.helper';
import {
  formatProjectConfig,
  sanitizeProjectConfig,
} from './EditAccessibilityPage.helper';
import {
  AntTab,
  AntTabs,
  StyledTableCell,
  ThemeContainer,
  ThemesLabel,
  ThemesTitle,
} from './EditAccessibilityPage.styled';
import { AccessibilitySettingsItem } from './modules/accessibilitySettingsItem/AccessibilitySettingsItem';
import { ComponentSettingsItem } from './modules/ComponentSettingsItem/ComponentSettingsItem';

export const EditAccessibilityPage = () => {
  const { data: themes = [] } = useGetThemes(['sort=*name']);
  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'name.regexp=carte',
  ]);
  const updateProjectConfigs = useUpdateProjectConfigs();

  const [activeTab, setActiveTab] = React.useState<string>();
  const [selectedComboVariant, setSelectedComboVariant] = useState<string>();
  const { isViewOnly } = useUserRole();

  const { data: projectConfig, isLoading: isLoadingGlobalConfig } =
    useGetProjectConfig();

  useEffect(() => {
    if (themes.length > 0) {
      setActiveTab(themes[0]?.name);
    }
  }, [themes]);

  useEffect(() => {
    if (productVariationTypes.length > 0) {
      setSelectedComboVariant(productVariationTypes[0]?.name);
    }
  }, [productVariationTypes]);

  const formMethods = useForm({
    values: formatProjectConfig(projectConfig, themes),
    resolver: ajvResolver(
      AccessibilitySettingsSchema as unknown as JSONSchemaType<ProjectConfigInterface>,
    ),
  });

  const confirmationModal = useModal();
  const publicationModal = useModal();

  const handleConfirm = async (
    updatedValue: Partial<ProjectConfigInterface>,
  ) => {
    const sanitizedProjectConfig = sanitizeProjectConfig(updatedValue);
    const projectConfigWithoutThemes = omit(
      sanitizedProjectConfig,
      'themes',
    ) as ProjectConfigInterface;

    if (projectConfig && themes) {
      const { themes: updatedThemes, ...updatedProjectConfig } =
        formMethods.formState.dirtyFields;
      const projectConfigPayloadData = Object.keys(updatedProjectConfig).map(
        (id) => ({
          resource: COLLECTIONS.PROJECT_CONFIGS,
          method: 'PATCH',
          id,
          data: {
            value:
              projectConfigWithoutThemes[id as keyof ProjectConfigInterface] !==
              undefined
                ? projectConfigWithoutThemes[id as keyof ProjectConfigInterface]
                : projectConfig[id as keyof ProjectConfigInterface],
            narrative: narrativeGenerators[COLLECTIONS.THEMES][
              USER_ACTIONS.UPDATE
            ](getObjectValue(ACCESSIBILITY_SETTINGS_LABELS, id, id)),
          } as { value: string | boolean | object },
        }),
      );

      const themesPayloadData = updatedThemes
        ? Object.keys(updatedThemes)
            .map((themeName) => {
              const theme = sanitizedProjectConfig.themes?.[themeName];
              const themeItem = themes.find(
                (themeItem) => themeItem.name === themeName,
              );

              if (theme && themeItem) {
                return {
                  resource: COLLECTIONS.THEMES,
                  method: 'PATCH',
                  id: themeItem?.id,
                  data: {
                    config: theme,
                    narrative:
                      narrativeGenerators[COLLECTIONS.THEMES][
                        USER_ACTIONS.UPDATE
                      ](themeName),
                  },
                };
              }

              return false;
            })
            .filter(Boolean)
        : [];

      const payload = {
        data: [...projectConfigPayloadData, ...themesPayloadData],
      };

      formMethods.clearErrors();

      await updateProjectConfigs.mutateAsync(
        payload.data as ProjectConfigTransaction[],
      );
    }
    confirmationModal.onCloseModal();
    publicationModal.onOpenModal();
  };

  const onClickSave = () => {
    formMethods.handleSubmit(handleConfirm)();
  };

  const onClickedCancel = () => {
    formMethods.reset(formatProjectConfig(projectConfig));
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const defaultBrandedColors = values(colorPickerColors);

  return (
    <FormProvider {...formMethods}>
      {isLoadingGlobalConfig ? (
        <Progress />
      ) : (
        <SubContainer>
          <Header
            title={lang.headerAccessibilityConfigTitle}
            icon={<InfoIcon />}
            iconText={lang.headerAccessibilityConfigIconText}
            breadcrumbItems={[
              {
                label: 'Home',
                link: '/',
              },
              {
                label: lang.headerAccessibilityConfigTitle,
              },
            ]}
            actionBar={isViewOnly && <ViewOnlyAlert />}
          />
          <SubContent>
            <SettingsCategory title={lang.autoScale}>
              <Controller
                name="autoscale"
                control={formMethods.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <SwitchButtonStyle>
                    <SwitchButtonWidget
                      {...field}
                      ref={ref}
                      options={{
                        leftLabel: 'Disable',
                        rightLabel: 'Enable',
                      }}
                      value={field.value}
                      label="Do you want to enable autoscale?"
                      onChange={(value) => field.onChange(value)}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? 'Required' : false}
                      disabled={isViewOnly}
                    />
                  </SwitchButtonStyle>
                )}
              />
            </SettingsCategory>

            <SettingsCategory title={lang.defaultComponentTheme}>
              <ThemesTitle>{lang.whichComponentThemesDescription}</ThemesTitle>
              <ThemesTitle>{lang.channelType}</ThemesTitle>
              {!isEmpty(themes) && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <ThemesLabel>{lang.indoor}</ThemesLabel>
                      <Controller
                        name="indoorDefaultTheme"
                        control={formMethods.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            selectOptions={generateOptions(
                              themes,
                              'name',
                              'name',
                            )}
                            onChange={(value) => field.onChange(value)}
                            hasError={Boolean(fieldState.error)}
                            error={fieldState.error?.message}
                            disabled={isViewOnly}
                            menuItemWidth={200}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ThemesLabel>{lang.outdoor}</ThemesLabel>
                      <Controller
                        name="outdoorDefaultTheme"
                        control={formMethods.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            selectOptions={generateOptions(
                              themes,
                              'name',
                              'name',
                            )}
                            onChange={(value) => field.onChange(value)}
                            hasError={Boolean(fieldState.error)}
                            error={fieldState.error?.message}
                            disabled={isViewOnly}
                            menuItemWidth={200}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ThemesLabel>{lang.mcCafe}</ThemesLabel>
                      <Controller
                        name="mcCafeDefaultTheme"
                        control={formMethods.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            selectOptions={generateOptions(
                              themes,
                              'name',
                              'name',
                            )}
                            onChange={(value) => field.onChange(value)}
                            hasError={Boolean(fieldState.error)}
                            error={fieldState.error?.message}
                            disabled={isViewOnly}
                            menuItemWidth={200}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ThemesLabel>{lang.digitalMerchandiser}</ThemesLabel>
                      <Controller
                        name="digitalMerchandiserDefaultTheme"
                        control={formMethods.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            selectOptions={generateOptions(
                              themes,
                              'name',
                              'name',
                            )}
                            onChange={(value) => field.onChange(value)}
                            hasError={Boolean(fieldState.error)}
                            error={fieldState.error?.message}
                            disabled={isViewOnly}
                            menuItemWidth={200}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </SettingsCategory>

            <SettingsCategory title={lang.themes}>
              <ThemesTitle>{lang.accessibilitySettingDefaultTheme}</ThemesTitle>

              {activeTab && (
                <AntTabs
                  value={activeTab}
                  onChange={handleChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Theme Tabs">
                  {themes.map((theme) => {
                    const hasError = get(
                      formMethods,
                      `formState.errors.themes.${theme.name}`,
                    );

                    return (
                      <AntTab
                        key={theme.id}
                        label={theme.name}
                        value={theme.name}
                        haserror={hasError}
                      />
                    );
                  })}
                </AntTabs>
              )}

              <ThemeContainer>
                <TableHead>
                  <TableRow>
                    <StyledTableCell width="30%">
                      {lang.baseSettings}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Grid container spacing={6}>
                        <Grid item xs={2}>
                          {lang.color}
                        </Grid>

                        <Grid item xs={3}>
                          {lang.fontFamily}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.fontSize}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.lineHeight}
                        </Grid>
                      </Grid>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <AccessibilitySettingsItem
                    label=""
                    colorPath={`themes.${activeTab}.color`}
                    fontFamilyPath={`themes.${activeTab}.font`}
                    fontSizePath={`themes.${activeTab}.fontSize`}
                    lineHeightPath={`themes.${activeTab}.lineHeight`}
                    isViewOnly={isViewOnly}
                  />
                </TableBody>

                <TableHead>
                  <TableRow>
                    <StyledTableCell width="30%">
                      {lang.componentSettings}
                    </StyledTableCell>

                    <StyledTableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  <ComponentSettingsItem
                    label={lang.margin}
                    marginPath={`themes.${activeTab}.margin`}
                    isViewOnly={isViewOnly}
                  />
                  <ComponentSettingsItem
                    label={lang.padding}
                    marginPath={`themes.${activeTab}.padding`}
                    isViewOnly={isViewOnly}
                  />
                </TableBody>

                <TableHead>
                  <TableRow>
                    <StyledTableCell width="30%">
                      {lang.configuration}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Grid container spacing={6}>
                        <Grid item xs={2}>
                          {lang.color}
                        </Grid>

                        <Grid item xs={3}>
                          {lang.fontFamily}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.fontSize}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.lineHeight}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.height}
                        </Grid>
                      </Grid>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <AccessibilitySettingsItem
                    label={lang.header}
                    colorPath={`themes.${activeTab}.header.color`}
                    fontFamilyPath={`themes.${activeTab}.header.font`}
                    fontSizePath={`themes.${activeTab}.header.fontSize`}
                    lineHeightPath={`themes.${activeTab}.header.lineHeight`}
                    heightPath={`themes.${activeTab}.header.height`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.headerDescription}
                    colorPath={`themes.${activeTab}.headerDescription.color`}
                    fontFamilyPath={`themes.${activeTab}.headerDescription.font`}
                    fontSizePath={`themes.${activeTab}.headerDescription.fontSize`}
                    lineHeightPath={`themes.${activeTab}.headerDescription.lineHeight`}
                    heightPath={`themes.${activeTab}.headerDescription.height`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.subheaderAndPill}
                    colorPath={`themes.${activeTab}.subheader.color`}
                    fontFamilyPath={`themes.${activeTab}.subheader.font`}
                    fontSizePath={`themes.${activeTab}.subheader.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subheader.lineHeight`}
                    backgroundPath={`themes.${activeTab}.subheader.backgroundColor`}
                    borderColorPath={`themes.${activeTab}.subheader.border.color`}
                    borderRadiusPath={`themes.${activeTab}.subheader.borderRadius`}
                    heightPath={`themes.${activeTab}.subheader.height`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.subheaderDescription}
                    colorPath={`themes.${activeTab}.subheaderDescription.color`}
                    fontFamilyPath={`themes.${activeTab}.subheaderDescription.font`}
                    fontSizePath={`themes.${activeTab}.subheaderDescription.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subheaderDescription.lineHeight`}
                    heightPath={`themes.${activeTab}.subheaderDescription.height`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.componentDisclaimer}
                    colorPath={`themes.${activeTab}.disclaimer.color`}
                    fontFamilyPath={`themes.${activeTab}.disclaimer.font`}
                    fontSizePath={`themes.${activeTab}.disclaimer.fontSize`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.disclaimerComponent}
                    colorPath={`themes.${activeTab}.disclaimerComponent.color`}
                    fontFamilyPath={`themes.${activeTab}.disclaimerComponent.font`}
                    fontSizePath={`themes.${activeTab}.disclaimerComponent.fontSize`}
                    lineHeightPath={`themes.${activeTab}.disclaimerComponent.lineHeight`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.productName}
                    colorPath={`themes.${activeTab}.products.color`}
                    fontFamilyPath={`themes.${activeTab}.products.font`}
                    fontSizePath={`themes.${activeTab}.products.fontSize`}
                    lineHeightPath={`themes.${activeTab}.products.lineHeight`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.sizeLabel}
                    colorPath={`themes.${activeTab}.subproducts.label.color`}
                    fontFamilyPath={`themes.${activeTab}.subproducts.label.font`}
                    fontSizePath={`themes.${activeTab}.subproducts.label.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subproducts.label.lineHeight`}
                    backgroundPath={`themes.${activeTab}.subproducts.label.backgroundColor`}
                    borderStylePath={`themes.${activeTab}.subproducts.label.border.style`}
                    borderWidthPath={`themes.${activeTab}.subproducts.label.border.width`}
                    borderColorPath={`themes.${activeTab}.subproducts.label.border.color`}
                    heightPath={`themes.${activeTab}.subproducts.label.height`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.productFlavor}
                    colorPath={`themes.${activeTab}.subproducts.flavor.color`}
                    fontFamilyPath={`themes.${activeTab}.subproducts.flavor.font`}
                    fontSizePath={`themes.${activeTab}.subproducts.flavor.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subproducts.flavor.lineHeight`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.productPrice}
                    colorPath={`themes.${activeTab}.subproducts.price.color`}
                    fontFamilyPath={`themes.${activeTab}.subproducts.price.font`}
                    fontSizePath={`themes.${activeTab}.subproducts.price.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subproducts.price.lineHeight`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.productNutrition}
                    colorPath={`themes.${activeTab}.subproducts.nutrition.color`}
                    fontFamilyPath={`themes.${activeTab}.subproducts.nutrition.font`}
                    fontSizePath={`themes.${activeTab}.subproducts.nutrition.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subproducts.nutrition.lineHeight`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.productDisclaimer}
                    colorPath={`themes.${activeTab}.subproducts.disclaimer.color`}
                    fontFamilyPath={`themes.${activeTab}.subproducts.disclaimer.font`}
                    fontSizePath={`themes.${activeTab}.subproducts.disclaimer.fontSize`}
                    lineHeightPath={`themes.${activeTab}.subproducts.disclaimer.lineHeight`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />
                </TableBody>

                <TableHead>
                  <TableRow>
                    <StyledTableCell width="30%">
                      {lang.comboComponentStyling}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Grid container spacing={6}>
                        <Grid item xs={2}>
                          {lang.color}
                        </Grid>

                        <Grid item xs={3}>
                          {lang.fontFamily}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.fontSize}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.lineHeight}
                        </Grid>

                        <Grid item xs={2}>
                          {lang.height}
                        </Grid>
                      </Grid>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <AccessibilitySettingsItem
                    label={lang.comboOrderNumber}
                    colorPath={`themes.${activeTab}.badges.groupBadge.color`}
                    fontFamilyPath={`themes.${activeTab}.badges.groupBadge.font`}
                    fontSizePath={`themes.${activeTab}.badges.groupBadge.fontSize`}
                    lineHeightPath={`themes.${activeTab}.badges.groupBadge.lineHeight`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.comboBadgeAndBar}
                    colorPath={`themes.${activeTab}.badges.itemBadge.color`}
                    fontFamilyPath={`themes.${activeTab}.badges.itemBadge.font`}
                    fontSizePath={`themes.${activeTab}.badges.itemBadge.fontSize`}
                    lineHeightPath={`themes.${activeTab}.badges.itemBadge.lineHeight`}
                    backgroundPath={`themes.${activeTab}.badges.itemBadge.backgroundColor`}
                    barWidthPath={`themes.${activeTab}.badges.itemBadge.barWidth`}
                    sizePath={`themes.${activeTab}.badges.itemBadge.size`}
                    isViewOnly={isViewOnly}
                  />

                  <AccessibilitySettingsItem
                    label={lang.imageBadge}
                    colorPath={`themes.${activeTab}.badges.imageBadge.color`}
                    fontFamilyPath={`themes.${activeTab}.badges.imageBadge.font`}
                    fontSizePath={`themes.${activeTab}.badges.imageBadge.fontSize`}
                    backgroundPath={`themes.${activeTab}.badges.imageBadge.backgroundColor`}
                    sizePath={`themes.${activeTab}.badges.imageBadge.size`}
                    colorPickerMode="block"
                    defaultBrandedColors={defaultBrandedColors}
                    isViewOnly={isViewOnly}
                  />

                  <TableRow>
                    <StyledTableCell width="30%">
                      <SelectDropdownWidget
                        label={lang.selectComboVariant}
                        value={selectedComboVariant}
                        selectOptions={generateOptions(
                          productVariationTypes,
                          'name',
                          'name',
                        )}
                        onChange={setSelectedComboVariant as any}
                      />
                    </StyledTableCell>
                    <StyledTableCell />
                  </TableRow>

                  {selectedComboVariant && (
                    <>
                      <AccessibilitySettingsItem
                        label={lang.variantLabel}
                        colorPath={`themes.${activeTab}.category.${selectedComboVariant}.label.color`}
                        fontFamilyPath={`themes.${activeTab}.category.${selectedComboVariant}.label.font`}
                        fontSizePath={`themes.${activeTab}.category.${selectedComboVariant}.label.fontSize`}
                        lineHeightPath={`themes.${activeTab}.category.${selectedComboVariant}.label.lineHeight`}
                        isViewOnly={isViewOnly}
                      />

                      <AccessibilitySettingsItem
                        label={lang.variantPrice}
                        colorPath={`themes.${activeTab}.category.${selectedComboVariant}.price.color`}
                        fontFamilyPath={`themes.${activeTab}.category.${selectedComboVariant}.price.font`}
                        fontSizePath={`themes.${activeTab}.category.${selectedComboVariant}.price.fontSize`}
                        lineHeightPath={`themes.${activeTab}.category.${selectedComboVariant}.price.lineHeight`}
                        colorPickerMode="block"
                        defaultBrandedColors={defaultBrandedColors}
                        isViewOnly={isViewOnly}
                      />

                      <AccessibilitySettingsItem
                        label={lang.variantNutrition}
                        colorPath={`themes.${activeTab}.category.${selectedComboVariant}.nutrition.color`}
                        fontFamilyPath={`themes.${activeTab}.category.${selectedComboVariant}.nutrition.font`}
                        fontSizePath={`themes.${activeTab}.category.${selectedComboVariant}.nutrition.fontSize`}
                        lineHeightPath={`themes.${activeTab}.category.${selectedComboVariant}.nutrition.lineHeight`}
                        colorPickerMode="block"
                        defaultBrandedColors={defaultBrandedColors}
                        isViewOnly={isViewOnly}
                      />
                    </>
                  )}
                </TableBody>
              </ThemeContainer>
            </SettingsCategory>
          </SubContent>

          {!isViewOnly && (
            <FooterContainer>
              <CustomButton
                variant="text"
                onClick={onClickedCancel}
                disabled={
                  formMethods.formState.isLoading ||
                  isLoadingGlobalConfig ||
                  formMethods.formState.isSubmitting ||
                  formMethods.formState.isValidating
                }>
                {lang.cancel}
              </CustomButton>
              <Button
                variant="contained"
                onClick={confirmationModal.onOpenModal}
                disabled={
                  formMethods.formState.isLoading ||
                  isLoadingGlobalConfig ||
                  formMethods.formState.isSubmitting ||
                  formMethods.formState.isValidating
                }>
                {lang.save}
              </Button>
            </FooterContainer>
          )}

          <SimpleDialog
            open={confirmationModal.open}
            cancelText={lang.cancel}
            confirmText={lang.confirm}
            title={lang.titleTextSave}
            subTitle={lang.subTitlePublishConfirm}
            onClose={confirmationModal.onCloseModal}
            onClickCancel={confirmationModal.onCloseModal}
            onClickConfirm={onClickSave}
            modalSize="sm"
            isLoading={
              formMethods.formState.isLoading ||
              isLoadingGlobalConfig ||
              updateProjectConfigs.isLoading ||
              formMethods.formState.isSubmitting ||
              formMethods.formState.isValidating
            }
          />

          {publicationModal.open && isEmpty(formMethods.formState.errors) && (
            <PublicationModal
              open={publicationModal.open}
              onClose={publicationModal.onCloseModal}
            />
          )}
        </SubContainer>
      )}
    </FormProvider>
  );
};
